import React, { useState, useEffect } from 'react';
import { withLocalize } from 'react-localize-redux';
import HeaderPage from '../HeaderPage/HeaderPage';
import Footer from '../../../_Common/Footer';
import './ShoppingListPage.css';

const ShoppingListPage = () => {
    const [formData, setFormData] = useState({});
    const [userName, setUserName] = useState('');
    const [shoppingList, setShoppingList] = useState({});
    const [checkedItems, setCheckedItems] = useState({});
    const [mealPlansList, setMealPlansList] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});
    const [savedListItems, setSavedListItems] = useState({});

    const safeParseJson = (str) => {
        try {
            const parsed = JSON.parse(str);
            if (typeof parsed === 'string') {
                return JSON.parse(parsed);
            }
            return parsed;
        } catch {
            return {};
        }
    };

    useEffect(() => {
        document.title = "MyMenuMoonshot - Shopping List";

        const fetchData = async () => {
            try {
                const storedUser = localStorage.getItem('userDetails');
                if (!storedUser) return;

                const user = JSON.parse(storedUser);
                const fullName = `${capitalize(user.sFirstName)}`;
                setFormData(user);
                setUserName(fullName);

                // Fetch checked items first
                const checkListResponse = await fetch(`https://nutritrustapi.azurewebsites.net/api/Menu/GetShoppingCheckList?sUserId=${user.sUserId}`);
                const checkListString = await checkListResponse.text();
                console.log('Raw checklist string:', checkListString);
                const parsedCheckList = safeParseJson(checkListString);
                setCheckedItems(parsedCheckList);

                // Fetch shopping list data
                const response = await fetch(`https://nutritrustapi.azurewebsites.net/api/Menu/GetMealPlans?sUserId=${user.sUserId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': 'A3BDF392-1EA2-4A8E-BD67-2B985D4CBD6C',
                        'aspnetuserid': user.sUserId,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch meal plans');
                }

                const mealPlansRaw = await response.json();
                const mealPlans = mealPlansRaw?.meals || [];
                setMealPlansList(mealPlans);

                const consolidatedMap = new Map();

                mealPlans.forEach(meal => {
                    const mealQuantity = meal.quantity ?? 1;

                    meal.shopping_list?.forEach(item => {
                        const key = item.ingredient_alias;
                        if (!key) return;

                        const itemQuantity = parseFloat(item.quantity) || 0;
                        const adjustedQuantity = itemQuantity * mealQuantity;

                        const existing = consolidatedMap.get(key);

                        if (existing) {
                            existing.quantity += adjustedQuantity;
                        } else {
                            consolidatedMap.set(key, {
                                menu_item_name: meal.name,
                                menu_item_id: item.menu_item_id,
                                ingredient_alias: item.ingredient_alias,
                                aum: item.aum || '',
                                quantity: adjustedQuantity,
                                affiliate_link: item.affiliate_link || '',
                                categories: item.categories || []
                            });
                        }
                    });
                });

                const grouped = {};

                consolidatedMap.forEach(item => {
                    const categories = item.categories.length ? item.categories : [{ name: 'Uncategorized' }];
                    categories.forEach(cat => {
                        const category = cat.name;
                        if (category === 'Recipes') return;

                        if (!grouped[category]) grouped[category] = [];
                        grouped[category].push({
                            menu_item_name: item.menu_item_name,
                            menu_item_id: item.menu_item_id,
                            ingredient_alias: item.ingredient_alias,
                            quantity: item.quantity,
                            affiliate_link: item.affiliate_link,
                            aum: item.aum
                        });
                    });
                });

                const sortedGrouped = {};
                Object.keys(grouped)
                    .sort()
                    .forEach(category => {
                        sortedGrouped[category] = grouped[category].sort((a, b) => a.ingredient_alias.localeCompare(b.ingredient_alias));
                    });

                setShoppingList(sortedGrouped);

            } catch (error) {
                console.error('Error loading shopping list:', error);
            }
        };

        fetchData();
    }, []);

    const capitalize = (str) =>
        typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

    const handleCheckboxToggle = async (itemKey) => {
        const updated = {
            ...checkedItems,
            [itemKey]: !checkedItems[itemKey]
        };
        setCheckedItems(updated);
        localStorage.setItem('shoppingCheckList', JSON.stringify(updated));

        const storedUser = localStorage.getItem('userDetails');
        if (!storedUser) return;
        const user = JSON.parse(storedUser);

        try {
            await fetch('https://nutritrustapi.azurewebsites.net/api/Menu/SaveShoppingCheckList', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': 'A3BDF392-1EA2-4A8E-BD67-2B985D4CBD6C',
                    'aspnetuserid': user.sUserId
                },
                body: JSON.stringify({
                    sUserId: user.sUserId,
                    shoppingCheckList: JSON.stringify(updated)
                })
            });
        } catch (err) {
            console.error("Failed to save checklist:", err);
        }
    };

    const renderMealPlans = () => {
        return mealPlansList
            .filter(meal => Array.isArray(meal.shopping_list) && meal.shopping_list.length > 0)
            .map(meal => {
                const imageUrl = meal.image ? "https://res.cloudinary.com" + meal.image : '';
                const linkUrl = `/menu-item/${meal.menu_item_id}/611/${meal.id}`;

                return (
                    <div key={meal.menu_item_id} className="meal-plan">
                        {imageUrl && (
                            <a href={linkUrl}>
                                <img
                                    src={imageUrl}
                                    alt={meal.name}
                                    className="meal-plan-image"
                                    style={{ cursor: 'pointer' }}
                                />
                            </a>
                        )}
                        <h4 style={{ cursor: 'pointer' }}>{meal.name}</h4>
                    </div>
                );
            });
    };

    const renderShoppingList = () => {
        return Object.entries(shoppingList).map(([category, items]) => (
            <div key={category} className="shopping-category">
                <h4 className='shopping_list_category'>{category}</h4>
                <ul>
                    {items.map(item => {
                        const itemKey = `${item.menu_item_id}_${item.ingredient_alias}`;
                        const hasLink = item.affiliate_link && item.affiliate_link.trim() !== "";

                        return (
                            <li
                                key={itemKey}
                                className={`shopping_list_item ${checkedItems[itemKey] ? 'checked' : ''}`}
                                //style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}
                            >
                                <input
                                    type="checkbox"
                                    checked={!!checkedItems[itemKey]}
                                    onChange={() => handleCheckboxToggle(itemKey)}
                                    //style={{ marginRight: '8px' }}
                                />

                                {/* Cart Icon or Placeholder to maintain alignment */}
                                {hasLink ? (
                                    <a
                                        href={item.affiliate_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="cart-icon-wrapper"
                                        //style={{ display: 'inline-block', width: '25px', textAlign: 'center', marginRight: '8px' }}
                                    >
                                        <i className="fa fa-cart-shopping fa-small shopping_cart_icon"></i>
                                    </a>
                                ) : (
                                    <span
                                        className="cart-icon-placeholder"
                                        //style={{ display: 'inline-block', width: '25px', marginRight: '8px' }}
                                    ></span>
                                )}

                                <span>
                                    {item.ingredient_alias} - {item.quantity} {item.aum}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        ));
    };

    return (
        <div className="App ShoppingListPage">
            <HeaderPage setFormData={formData} />
            <div className="container">
                <div className="row">
                    <div className="col-md-12">
                        <div className="inner_page_innersec">
                            <h2>{userName}'s Shopping List</h2>
                            <div
                                className="continue_search_landing event active shopping_btns"
                                onClick={e => {
                                    const confirmed = window.confirm("A new shopping list will be created. Are you sure you want to continue?");
                                    if (confirmed) {
                                        localStorage.removeItem('shoppingList');
                                        setSelectedItems({});
                                        setSavedListItems({});
                                        window.location.replace('/meal-plan?bNewShoppinglist=true');
                                    }
                                }}
                            >
                                <i className="fa fa-cart-shopping fa-small"></i> New Shopping List
                            </div>
                            <div className="shopping-list">
                            <p>Shopping List Recipes:</p>
                                <div className="meal-plans-container">
                                    {renderMealPlans()}
                                </div>
                                <div>Check items off as you shop or click the <i className="fa fa-cart-shopping fa-small"></i> icon to order ingredients online from Amazon/Whole Foods Market.</div>
                                <br />
                                {renderShoppingList()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default withLocalize(ShoppingListPage);